import { useContext, useEffect } from 'react';

import { ONBOARDING_PERSONALIZED_TEMPLATES_V2 } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useIsEligibleForPersonalizedTemplatesV2 } from '@confluence/experiment-personalized-templates-v2/entry-points/useIsEligibleForPersonalizedTemplatesV2.ts';
import { useIsEligibleForNthUserHomeOnboardingRevamp } from '@confluence/experiment-personalized-templates-v2/entry-points/useIsEligibleForNthUserHomeOnboardingRevamp.ts';
import { useIsEligibleForUseCasesOnboardingHomeChanges } from '@confluence/experiment-personalized-templates-v2/entry-points/useIsEligibleForUseCasesOnboardingHomeChanges.ts';
import { isStableUIEnabledForTests } from '@confluence/e2e-url-override/entry-points/isStableUIEnabledForTests.ts';

import { useNthUserHomeOnboardingTakeoverV2Eligibility } from './useNthUserHomeOnboardingTakeoverV2Eligibility.ts';

export type OnboardingTakeoverType =
	| 'nth-user'
	| 'personalized-templates-v2'
	| 'personalized-templates-v2-xflow'
	| 'nth-user-home-onboarding-revamp'
	| 'use-cases-in-nth-user-onboarding';
/**
 * Hook to determine which onboarding takeover will be displayed.
 *
 * @returns The type of onboarding takeover, or `null` if no takeover.
 */
export function useOnboardingTakeoverType(): {
	onboardingTakeoverType: OnboardingTakeoverType | null;
	loading?: boolean;
} {
	const experienceTracker = useContext(ExperienceTrackerContext);

	const {
		isEligible: v2NthUserEligible,
		loading: v2NthUserLoading,
		isVisible: v2NthUserVisible,
	} = useNthUserHomeOnboardingTakeoverV2Eligibility();

	const {
		isEligibleForLand: ptv2Eligible,
		isEligibleForXflow: ptv2EligibleXflow,
		loading: ptv2Loading,
		isVisible: isPTV2Visible,
	} = useIsEligibleForPersonalizedTemplatesV2();

	const {
		isEligible: nthUserHomeOnboardingRevampEligible,
		loading: nthUserHomeOnboardingRevampLoading,
		isVisible: nthUserHomeOnboardingRevampVisible,
	} = useIsEligibleForNthUserHomeOnboardingRevamp();

	const { loading: useCasesLoading, isEligible: isEligibleForUseCasesHomeChanges } =
		useIsEligibleForUseCasesOnboardingHomeChanges();

	useEffect(() => {
		if (!ptv2Loading && !(ptv2Eligible || ptv2EligibleXflow)) {
			experienceTracker.abort({
				name: ONBOARDING_PERSONALIZED_TEMPLATES_V2,
				reason: 'Not eligible for v2 takeover',
			});
		}
	}, [ptv2Eligible, ptv2EligibleXflow, ptv2Loading, experienceTracker]);

	// Early return for loading states
	const loading = [
		ptv2Loading,
		nthUserHomeOnboardingRevampLoading,
		useCasesLoading,
		v2NthUserLoading,
	];

	if (loading.some(Boolean)) {
		return { onboardingTakeoverType: null, loading: true };
	}

	if (isStableUIEnabledForTests()) {
		return { onboardingTakeoverType: null };
	}

	if (isEligibleForUseCasesHomeChanges) {
		return { onboardingTakeoverType: 'use-cases-in-nth-user-onboarding' };
	}

	// Check for nth user home onboarding revamp
	if (
		nthUserHomeOnboardingRevampEligible &&
		expValEquals('cc-nth-user-home-onboarding-revamp', 'cohort', 'experiment')
	) {
		if (nthUserHomeOnboardingRevampVisible) {
			return { onboardingTakeoverType: 'nth-user-home-onboarding-revamp' };
		}
		return { onboardingTakeoverType: null };
	}

	if (ptv2Eligible) {
		return isPTV2Visible
			? { onboardingTakeoverType: 'personalized-templates-v2' }
			: { onboardingTakeoverType: null };
	} else if (ptv2EligibleXflow) {
		return isPTV2Visible
			? { onboardingTakeoverType: 'personalized-templates-v2-xflow' }
			: { onboardingTakeoverType: null };
	}

	if (v2NthUserEligible) {
		return v2NthUserVisible
			? { onboardingTakeoverType: 'nth-user' }
			: { onboardingTakeoverType: null };
	}

	return { onboardingTakeoverType: null };
}
